/**
 * Buy Now - AJAX Add to Cart Functionality
 * Handles adding products to cart without page reload
 * and triggers cart popup display
 */

class BuyNowHandler {
  constructor(container) {
    this.container = container;
    this.button = container.querySelector('.buy-now-button');
    
    if (this.button) {
      this.button.addEventListener('click', this.handleAddToCart.bind(this));
    }
    
    // Listen for Shopify's native variant changes (from product-info.js)
    if (typeof subscribe !== 'undefined' && typeof PUB_SUB_EVENTS !== 'undefined') {
      this.variantChangeUnsubscriber = subscribe(
        PUB_SUB_EVENTS.variantChange,
        this.handleShopifyVariantChange.bind(this)
      );
    }
    
    // Fallback: Listen for custom variant changes
    document.addEventListener('variant:changed', this.handleVariantChange.bind(this));
  }
  
  /**
   * Handle Add to Cart button click
   */
  async handleAddToCart(event) {
    event.preventDefault();
    
    const button = event.currentTarget;
    const variantId = button.dataset.variantId;
    const isAjaxCart = button.dataset.ajaxCart === 'true';
    
    if (!variantId || button.disabled) {
      return;
    }
    
    // Add loading state
    this.setLoadingState(true);
    
    try {
      if (isAjaxCart) {
        // AJAX Add to Cart
        const response = await fetch('/cart/add.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json'
          },
          body: JSON.stringify({
            id: variantId,
            quantity: 1
          })
        });
        
        if (!response.ok) {
          throw new Error('Failed to add to cart');
        }
        
        const data = await response.json();
        
        // Trigger cart update event
        this.triggerCartUpdate(data);
        
        // Show success feedback
        this.showSuccessFeedback();
        
      } else {
        // Standard form submission (fallback)
        window.location.href = `/cart/add?id=${variantId}&quantity=1`;
      }
      
    } catch (error) {
      console.error('Add to cart error:', error);
      this.showErrorFeedback();
    } finally {
      this.setLoadingState(false);
    }
  }
  
  /**
   * Handle Shopify's native variant change event (from product-info.js)
   */
  handleShopifyVariantChange({ data }) {
    const variant = data?.variant;
    
    if (!variant) return;
    
    // Update button variant ID
    if (this.button) {
      this.button.dataset.variantId = variant.id;
      
      // Update button disabled state
      if (variant.available) {
        this.button.removeAttribute('disabled');
      } else {
        this.button.setAttribute('disabled', 'disabled');
      }
    }
    
    // Update buy-now price display
    this.updatePrice(variant);
  }

  /**
   * Update the buy-now price display with the new variant price
   */
  updatePrice(variant) {
    const priceElement = this.container.querySelector('.price-main');
    if (!priceElement || variant.price == null) return;

    const formattedPrice = theme.Currency.formatMoney(variant.price, theme.shopSettings.moneyFormat);
    priceElement.innerHTML = `<price-money><bdi>${formattedPrice}</bdi></price-money>`;
    priceElement.dataset.price = variant.price;
  }
  
  /**
   * Handle variant change event (custom/fallback)
   */
  handleVariantChange(event) {
    const variant = event.detail?.variant;
    
    if (!variant) return;
    
    // Update button variant ID
    if (this.button) {
      this.button.dataset.variantId = variant.id;
      
      // Update button disabled state
      if (variant.available) {
        this.button.removeAttribute('disabled');
      } else {
        this.button.setAttribute('disabled', 'disabled');
      }
    }
    
    // Update buy-now price display
    this.updatePrice(variant);
  }
  
  /**
   * Set loading state on button
   */
  setLoadingState(isLoading) {
    if (!this.button) return;
    
    if (isLoading) {
      this.button.classList.add('loading');
      this.button.disabled = true;
    } else {
      this.button.classList.remove('loading');
      // Only re-enable if variant is available
      const variantId = this.button.dataset.variantId;
      if (variantId) {
        this.button.disabled = false;
      }
    }
  }
  
  /**
   * Trigger cart update event for cart drawer/popup
   */
  triggerCartUpdate(cartData) {
    // Dispatch custom event for cart update
    document.dispatchEvent(new CustomEvent('cart:updated', {
      detail: { cart: cartData }
    }));
    
    // Also trigger Shopify theme cart update if available
    if (window.Shopify && window.Shopify.theme) {
      document.dispatchEvent(new CustomEvent('cart:refresh'));
    }
    
    // Trigger cart drawer open (if using Shopify's cart drawer)
    const cartDrawer = document.querySelector('cart-drawer');
    if (cartDrawer && typeof cartDrawer.open === 'function') {
      cartDrawer.open();
    }
    
    // Alternative: trigger cart notification
    const cartNotification = document.querySelector('cart-notification');
    if (cartNotification && typeof cartNotification.open === 'function') {
      cartNotification.open();
    }
  }
  
  /**
   * Show success feedback
   */
  showSuccessFeedback() {
    const span = this.button.querySelector('span');
    if (!span) return;
    
    const originalText = span.textContent;
    span.textContent = 'Added!';
    
    setTimeout(() => {
      span.textContent = originalText;
    }, 2000);
  }
  
  /**
   * Show error feedback
   */
  showErrorFeedback() {
    const span = this.button.querySelector('span');
    if (!span) return;
    
    const originalText = span.textContent;
    span.textContent = 'Error!';
    
    setTimeout(() => {
      span.textContent = originalText;
    }, 2000);
  }
}

// Initialize Buy Now handlers
function initBuyNowButtons() {
  const buyNowContainers = document.querySelectorAll('.product-buy-now');
  buyNowContainers.forEach(container => {
    if (!container.dataset.initialized) {
      new BuyNowHandler(container);
      container.dataset.initialized = 'true';
    }
  });
}

// Initialize on DOM ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initBuyNowButtons);
} else {
  initBuyNowButtons();
}

// Re-initialize when Shopify sections are updated
document.addEventListener('shopify:section:load', initBuyNowButtons);

